if (!customElements.get('sticky-atc')) {
  // Block scoped, so loading this file twice (section re-render in the theme editor) is harmless
  const COLLAPSED_KEY = 'ichess-sticky-atc-collapsed';

  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.priceSource = document.getElementById(`price-${this.sectionId}`);
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        if (!this.productInfo || !this.mainButton) return;

        this.button = this.querySelector('[data-sticky-atc-button]');
        this.label = this.querySelector('[data-sticky-atc-label]');
        this.variantText = this.querySelector('[data-sticky-atc-variant]');
        this.finalPrice = this.querySelector('[data-sticky-atc-final]');
        this.comparePrice = this.querySelector('[data-sticky-atc-compare]');

        this.button.addEventListener('click', this.onButtonClick.bind(this));
        this.querySelectorAll('[data-sticky-atc-top]').forEach((element) =>
          element.addEventListener('click', this.onTopClick.bind(this))
        );

        // Desktop: the customer can fold the card into a small tab; the choice is kept for the visit
        this.collapseButton = this.querySelector('[data-sticky-atc-collapse]');
        this.expandButton = this.querySelector('[data-sticky-atc-expand]');
        this.collapseButton?.addEventListener('click', () => this.setCollapsed(true, true));
        this.expandButton?.addEventListener('click', () => this.setCollapsed(false, true));
        this.setCollapsed(this.readCollapsed(), false);

        // Visible only after the main button has scrolled out of view above the screen. A scroll check
        // (once per frame) instead of IntersectionObserver: a fast fling or a jump can move the button from
        // below the screen to above it without it ever intersecting, and the observer would never fire.
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateVisibility();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.updateVisibility();

        // The main button and the price are the source of truth: copy their state whenever they change.
        this.mirrorObserver = new MutationObserver(() => this.scheduleSync());
        this.mirrorObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
        if (this.priceSource) {
          this.mirrorObserver.observe(this.priceSource, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;
          // Once the customer has picked a variant, the bar can add it directly.
          this.dataset.mode = 'add';
          if (this.variantText && data.variant) this.variantText.textContent = data.variant.title;
          this.scheduleSync();
        });

        this.sync();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mirrorObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        document.body.classList.remove('sticky-atc-visible');
      }

      updateVisibility() {
        // The bar can be switched off for mobile or desktop (display: none): then it never counts as shown.
        const available = this.getClientRects().length > 0;
        const buttonPassed = this.mainButton.getBoundingClientRect().bottom < 0;
        this.setVisible(available && buttonPassed && !this.footerInView());
      }

      // Desktop: the card floats over the left edge of the page; at the footer it steps aside so the
      // footer links stay readable (mobile keeps the bar and pads the page instead).
      footerInView() {
        if (!this.desktopQuery.matches) return false;
        const footer = document.querySelector('.shopify-section-group-footer-group');
        return Boolean(footer) && footer.getBoundingClientRect().top < window.innerHeight;
      }

      setVisible(visible) {
        if (this.classList.contains('is-visible') === visible) return;
        if (visible) this.liftFloatingBadge();
        this.classList.toggle('is-visible', visible);
        document.body.classList.toggle('sticky-atc-visible', visible);
      }

      // Google's store badge sits fixed in a bottom corner (its offset is set inline with !important,
      // so CSS cannot move it). Measure how far it reaches into the bar and lift it by that much.
      liftFloatingBadge() {
        const badge = document.getElementById('google-merchantwidget-iframe-wrapper');
        if (!badge) return;
        const spaceBelowBadge = window.innerHeight - badge.getBoundingClientRect().bottom;
        const lift = Math.max(0, this.offsetHeight + 8 - spaceBelowBadge);
        document.documentElement.style.setProperty('--sticky-atc-badge-lift', `${lift}px`);
      }

      readCollapsed() {
        try {
          return sessionStorage.getItem(COLLAPSED_KEY) === '1';
        } catch (e) {
          return false;
        }
      }

      setCollapsed(collapsed, byCustomer) {
        this.classList.toggle('is-collapsed', collapsed);
        this.collapseButton?.setAttribute('aria-expanded', String(!collapsed));
        if (!byCustomer) return;
        try {
          sessionStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
        } catch (e) {}
        // Keep keyboard focus on the control that is now shown
        (collapsed ? this.expandButton : this.button)?.focus();
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
        });
      }

      sync() {
        this.syncPrice();

        if (this.dataset.mode !== 'add') {
          this.label.textContent = this.dataset.chooseLabel;
          this.button.disabled = false;
          this.button.classList.remove('loading');
          return;
        }

        // Same text, availability and loading state as the main Add to cart button.
        const mainLabel = this.mainButton.querySelector('span');
        if (mainLabel) this.label.textContent = mainLabel.textContent.trim();
        this.button.disabled = this.mainButton.disabled;
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        this.button
          .querySelector('.loading__spinner')
          ?.classList.toggle('hidden', !this.mainButton.classList.contains('loading'));
      }

      syncPrice() {
        const price = this.priceSource?.querySelector('.price');
        if (!price) return;

        let final = '';
        let compare = '';
        if (price.classList.contains('price--affiliate-preview')) {
          // Promo or partner price preview rendered by affiliate-pricing.js
          final = price.querySelector('[data-affiliate-preview-final]')?.textContent;
          compare = price.querySelector('[data-affiliate-preview-compare]')?.textContent;
        } else if (price.classList.contains('price--on-sale')) {
          final = price.querySelector('.price__sale .price-item--last')?.textContent;
          compare = price.querySelector('.price__sale s.price-item--regular')?.textContent;
        } else {
          final = price.querySelector('.price__regular .price-item--regular')?.textContent;
        }

        final = (final || '').trim();
        compare = (compare || '').trim();
        if (final) this.finalPrice.textContent = final;
        this.comparePrice.textContent = compare;
        this.comparePrice.hidden = !compare;
      }

      onButtonClick() {
        if (this.dataset.mode !== 'add') {
          this.scrollToOptions();
          return;
        }
        if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;

        // Submitting through the main form keeps one code path: same variant, cart drawer, errors.
        this.mainButton.click();
        this.revealErrors();
      }

      // Desktop: the photo and the title take the customer back to the top of the product
      onTopClick() {
        if (!this.desktopQuery.matches) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        // The bar hides on the way up; keyboard focus continues from the top of the page, not from nowhere
        document.getElementById('MainContent')?.focus({ preventScroll: true });
      }

      scrollToOptions() {
        const picker = this.productInfo.querySelector('variant-selects') || this.productInfo.querySelector('.product-form');
        if (!picker) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        picker.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        picker.querySelector('input:checked, select')?.focus({ preventScroll: true });
      }

      // An error (e.g. no stock left) is shown next to the main form, out of view; bring it into view.
      revealErrors() {
        const wrapper = this.productInfo.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        const observer = new MutationObserver(() => {
          if (wrapper.hasAttribute('hidden')) return;
          observer.disconnect();
          wrapper.scrollIntoView({ block: 'center' });
        });
        observer.observe(wrapper, { attributes: true, attributeFilter: ['hidden'] });
        setTimeout(() => observer.disconnect(), 10000);
      }
    }
  );
}
